import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { environment } from '../../../environments/environment';
import type { CompletedShipmentConfirmation } from '../../core/api/shipment/models/completed-shipment.model';
import { isValidCompletedShipment } from '../../core/api/shipment/models/completed-shipment.model';
import {
  formatPaymentCardBrand,
  resolvePaymentCardBrandIconSrc,
} from '../../core/api/shipment/models/create-shipment.model';
import { TranslatePipe, TranslationService } from '../../core/i18n';
import { ShipmentFlowStateService } from '../../core/services/shipment-flow-state.service';
import { formatCurrencyAmount } from '../service-step/sections/delivery-service-selection/delivery-service-matrix';
import {
  formatCancelDeadlineDate,
  formatShipmentCreationDate,
} from '../get-labels/utils/format-get-labels-dates';
import { buildGetLabelsSummaryViews } from '../get-labels/utils/build-get-labels-summary-views';
import {
  formatReviewAddressBlock,
  type ReviewAddressView,
} from '../review-and-pay/utils/map-review-address';

/**
 * Standalone, chrome-free print view of a completed shipment's details, opened in a new tab
 * from Get Labels. Reads the same session data as Get Labels and renders a static preview.
 * Print is on-demand via the Print/Download button — auto-print on load is avoided because
 * Chromium's system print dialog modal-blocks other tabs (including Get Labels) while open
 * (OM-744).
 */
@Component({
  selector: 'app-shipment-details-print',
  imports: [ButtonComponent, TranslatePipe, TypographyComponent],
  templateUrl: './shipment-details-print.html',
  styleUrl: './shipment-details-print.scss',
})
export class ShipmentDetailsPrint implements OnInit {
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly translation = inject(TranslationService);
  private readonly router = inject(Router);

  protected readonly confirmation = signal<CompletedShipmentConfirmation | null>(null);
  private readonly flowState = signal(this.shipmentFlowState.load());

  protected readonly formattedCreationDate = computed(() => {
    const completed = this.confirmation();
    return completed
      ? formatShipmentCreationDate(completed.shipmentCreationDate, this.translation.locale())
      : '';
  });

  protected readonly summaryViews = computed(() =>
    buildGetLabelsSummaryViews(
      this.flowState(),
      this.confirmation(),
      this.translation,
      environment.homeLocatorBaseUrl,
    ),
  );

  /**
   * Ship From / Ship To cards for print — single template loop avoids duplicated markup (OM-763 / Sonar).
   */
  protected readonly printAddressCards = computed(() => {
    const views = this.summaryViews();
    return [
      {
        testId: 'shipment-details-print-ship-from',
        titleKey: 'createShipment.reviewAndPay.cards.shipFrom',
        address: views.shipFromView,
      },
      {
        testId: 'shipment-details-print-ship-to',
        titleKey: 'createShipment.reviewAndPay.cards.shipTo',
        address: views.shipToView,
      },
    ] as const;
  });

  protected readonly paymentDisplay = computed(() => this.confirmation()?.paymentDisplay ?? null);

  /**
   * Resolves the drop-off / label-only note shown under the induction card title, matching the
   * Get Labels copy. The on-screen drop-off note is split around a locator link; the print sheet
   * has no clickable link, so the fragments are joined into one sentence. Pickup returns null
   * because its date, time, and location rows already describe the induction.
   */
  protected readonly inductionNote = computed(() => {
    const method = this.summaryViews().inductionView?.method;
    if (method === 'drop-off') {
      return [
        'createShipment.reviewAndPay.induction.dropOffBefore',
        'createShipment.reviewAndPay.induction.dropOffEmphasis',
        'createShipment.reviewAndPay.induction.dropOffAfter',
      ]
        .map((key) => this.translation.translate(key))
        .join('');
    }

    return method === 'label-only'
      ? this.translation.translate('createShipment.reviewAndPay.induction.labelOnlyBody')
      : null;
  });

  protected readonly formattedArrivalDate = computed(() => {
    const deliveryDate = this.summaryViews().selectedService?.deliveryDate;
    return deliveryDate ? formatCancelDeadlineDate(deliveryDate, this.translation.locale()) : '';
  });

  protected readonly formatCurrencyAmount = formatCurrencyAmount;
  protected readonly formatPaymentCardBrand = formatPaymentCardBrand;
  protected readonly resolvePaymentCardBrandIconSrc = resolvePaymentCardBrandIconSrc;

  /**
   * Compact Figma address lines for print (name → street → suite → locality → entry code).
   */
  protected addressBlockLines(address: ReviewAddressView): string[] {
    return formatReviewAddressBlock(address, {
      suiteAptLabel: this.translation.translate('createShipment.reviewAndPay.suiteAptLabel'),
      buzzerLabel: this.translation.translate('createShipment.reviewAndPay.buzzerLabel'),
    });
  }

  /**
   * Formats the print Service line. Lambda already returns names like "Purolator Express";
   * wizard names are short ("Express") and still use the "Purolator {{name}}" i18n prefix.
   *
   * @param displayName Service display name from Get Labels summary views
   */
  protected formatPrintServiceName(displayName: string): string {
    const trimmed = displayName.trim();
    if (/^purolator\b/i.test(trimmed)) {
      return trimmed;
    }

    return this.translation.translate('createShipment.shipmentDetailsPrint.serviceName', {
      name: trimmed,
    });
  }

  /**
   * Loads the completed-shipment confirmation from session; redirects home when it is missing
   * (e.g. the tab was opened without an active session).
   */
  ngOnInit(): void {
    const state = this.shipmentFlowState.load();
    const completed = state?.completedShipment;
    if (!isValidCompletedShipment(completed)) {
      this.router.navigateByUrl('/').catch(() => undefined);
      return;
    }

    this.flowState.set(state);
    this.confirmation.set(completed);
  }

  /**
   * Opens the browser's native print dialog, letting the user print or save the page as a PDF.
   * Paper size is hinted via CSS `@page { size: letter }` (8.5 × 11 in); browsers may still
   * allow the user to override that choice in the print UI.
   */
  protected triggerPrint(): void {
    window.print();
  }

  /**
   * Resolves an i18n label for a surcharge/accessorial label code (same keys as Service step).
   */
  protected surchargeLabelKey(labelCode: string): string {
    const normalizedLabelCode = labelCode === 'SIG' ? 'RES' : labelCode;
    return `createShipment.serviceStep.deliveryService.breakdown.surcharges.${normalizedLabelCode}`;
  }
}
